/**
 * Workload — what the spend was actually spent doing, and how much of it was
 * wasted.
 *
 * Two sources, both borrowed from the activity side of the platform:
 *
 *   * /dashboard/stats, whose request status counts honour the period filter —
 *     the emails and documents totals in that same response do NOT, so they are
 *     deliberately not rendered here.
 *   * /dashboard/job-stats, which is organisation-wide and all-time. It is
 *     labelled as such rather than quietly inheriting the period control.
 *
 * Failed jobs earn their place on a billing page as waste to watch, but not as
 * a charge: only completed document-analysis jobs are billed, so a failed one
 * costs the plan allowance nothing.
 */
import { AlertCircle, Activity, CheckCircle2, Clock, Loader2, Mail, XCircle } from 'lucide-react'
import { Card, EmptyState, HelpTip, Sparkline } from './BillingPrimitives'
import { formatInt } from '../../utils/billing'
import { percentParts } from '../../utils/apportion'
import { isAllTimeLabel } from './useBillingData'

const STATUS_ORDER = [
  { key: 'completed', label: 'Completed' },
  { key: 'reviewing', label: 'In review' },
  { key: 'processing', label: 'Processing' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Cancelled' },
]

const EMAIL_STATES = [
  { key: 'processed', label: 'Processed', bar: 'bg-emerald-500 dark:bg-emerald-400' },
  { key: 'processing', label: 'Processing', bar: 'bg-blue-500 dark:bg-blue-400' },
  { key: 'pending', label: 'Pending', bar: 'bg-amber-500 dark:bg-amber-400' },
  { key: 'failed', label: 'Failed', bar: 'bg-rose-500 dark:bg-rose-400' },
]

const JOB_TILES = [
  { key: 'completed', label: 'Completed', icon: CheckCircle2, tone: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'running', label: 'Running', icon: Loader2, tone: 'text-blue-600 dark:text-blue-400' },
  { key: 'pending', label: 'Queued', icon: Clock, tone: 'text-amber-600 dark:text-amber-400' },
  { key: 'failed', label: 'Failed', icon: XCircle, tone: 'text-rose-600 dark:text-rose-400' },
]

export default function WorkloadPanel({ workload, chart, periodLabel, emailsArePeriodScoped }) {
  const initialLoad = workload?.loading && !workload?.stats && !workload?.jobs
  // Once something has rendered, a period switch dims the existing cards
  // rather than replacing them with a skeleton or leaving them looking inert
  // while the new period's figures are still in flight.
  const refreshing = workload?.loading && !initialLoad

  if (initialLoad) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="skeleton h-56 rounded-xl" />
        <div className="skeleton h-56 rounded-xl" />
        <span className="sr-only" role="status">Loading workload</span>
      </div>
    )
  }

  if (!workload?.stats && !workload?.jobs) {
    return (
      <Card className="p-5">
        <EmptyState
          icon={AlertCircle}
          title="Workload data is unavailable"
          body="These panels read the platform's activity data rather than billing. Your plan and cost figures are unaffected."
        />
      </Card>
    )
  }

  const requests = workload.stats?.requests || {}
  const named = STATUS_ORDER.map(s => ({ ...s, count: requests[s.key] || 0, color: chart.status[s.key] }))

  // requests.total is the authoritative count for the period; the five named
  // buckets are not exhaustive. A request with status "failed" is real
  // production data on this org and falls into none of pending/processing/
  // reviewing/completed/cancelled — summing only the named buckets silently
  // dropped it from both the headline count and the proportion bar (36 real
  // requests displayed as 32). Anything the named buckets do not cover is
  // rolled into an honest "Other" segment rather than vanishing.
  const namedTotal = named.reduce((sum, s) => sum + s.count, 0)
  const otherCount = Math.max(0, (requests.total || 0) - namedTotal)
  const statuses = otherCount > 0
    ? [...named, { key: 'other', label: 'Other', count: otherCount, color: chart.neutral }]
    : named
  const statusTotal = requests.total ?? namedTotal
  // Whole-number shares of the period's requests that add up to exactly 100.
  const pcts = percentParts(statuses.map(s => s.count))
  const statusPct = new Map(statuses.map((s, i) => [s.key, pcts[i]]))

  const dates = workload.stats?.template_trend?.dates || []
  const daily = workload.stats?.daily_counts || []

  const jobs = workload.jobs || {}
  const jobTotal = jobs.total || 0
  const failureRate = jobTotal > 0 ? (jobs.failed / jobTotal) * 100 : 0

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 transition-opacity duration-200 ${refreshing ? 'opacity-50' : 'opacity-100'}`}>
      {refreshing && <span className="sr-only" role="status">Updating for the new period</span>}
      {/* ---- where the period's requests ended up ---- */}
      <Card className="p-5 animate-card-in" style={{ animationDelay: '40ms' }}>
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Where requests stand</h2>
          <HelpTip>
            Requests raised in the selected period, by current status. Work sitting in review has already
            consumed its pages: the allowance is spent at extraction, not at approval. “Other” covers
            statuses outside the everyday flow, such as a failed extraction. Failed extractions are not
            billed, so they do not count toward your allowance.
          </HelpTip>
        </div>
        {/* Job outcomes can load while the request counts fail: that is
            "unavailable", never "0 requests raised". */}
        {workload.stats && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4">
            {formatInt(statusTotal)} requests raised {isAllTimeLabel(periodLabel) ? 'in total' : `in the last ${periodLabel}`}
          </p>
        )}

        {!workload.stats ? (
          <EmptyState
            icon={AlertCircle}
            title="Request figures are unavailable"
            body="The request counts could not be loaded. Use Refresh to try again."
          />
        ) : statusTotal === 0 ? (
          <EmptyState icon={Activity} title="No requests in this period" />
        ) : (
          <>
            <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 gap-0.5 mb-4">
              {statuses.filter(s => s.count > 0).map(s => (
                <div
                  key={s.key}
                  className="transition-[width] duration-700 ease-out first:rounded-l-full last:rounded-r-full"
                  style={{ width: `${(s.count / statusTotal) * 100}%`, backgroundColor: s.color }}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {statuses.map(s => (
                <div key={s.key} className="flex items-center gap-2">
                  <i className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-[12px] text-gray-600 dark:text-gray-300">{s.label}</span>
                  <span className="ml-auto text-[12px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                    {formatInt(s.count)}
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-400 dark:text-gray-500 w-9 text-right">
                    {statusPct.get(s.key)}%
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {daily.length > 0 && (
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Daily rhythm</p>
              {/* This series is fixed at seven days by the endpoint and does not
                  follow the period control, so it says so rather than implying it. */}
              <p className="text-[10px] text-gray-400 dark:text-gray-500">Last 7 days</p>
            </div>
            <div className="mt-2">
              <Sparkline data={daily} color={chart.series[0]} height={34} />
            </div>
            {dates.length === daily.length && (
              <div className="flex justify-between mt-1 text-[10px] text-gray-400 dark:text-gray-500">
                <span>{dates[0]}</span>
                <span>{dates[dates.length - 1]}</span>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ---- processing health: failure is spend with nothing to show ---- */}
      <Card className="p-5 animate-card-in" style={{ animationDelay: '100ms' }}>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Processing health</h2>
          <HelpTip>
            Extraction jobs by outcome. A job that failed part-way has usually already read some of its pages
            on Azure, so failures are cost with nothing to show for it, worth watching even when the rate
            is low. Failed jobs are not billed against your plan.
          </HelpTip>
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">
            All time
          </span>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4">
          Organisation-wide job outcomes. This counter is not filtered by the period above.
        </p>

        {jobTotal === 0 ? (
          <EmptyState icon={Clock} title="No processing jobs recorded yet" />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {JOB_TILES.map(tile => (
                <div key={tile.key} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <tile.icon className={`w-3.5 h-3.5 ${tile.tone}`} />
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">{tile.label}</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100 tabular-nums mt-0.5">
                    {formatInt(jobs[tile.key])}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-baseline justify-between">
                <span className="text-[12px] text-gray-600 dark:text-gray-300">Failure rate</span>
                <span className={`text-lg font-bold tabular-nums ${
                  failureRate >= 5
                    ? 'text-rose-600 dark:text-rose-400'
                    : failureRate >= 1
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                }`}>
                  {failureRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 mt-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-700 ease-out ${
                    failureRate >= 5 ? 'bg-rose-500' : failureRate >= 1 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, Math.max(failureRate, failureRate > 0 ? 2 : 0))}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                {jobs.failed > 0
                  ? `${formatInt(jobs.failed)} failed of ${formatInt(jobTotal)} jobs. Failed jobs are not billed against your plan.`
                  : `No failures across ${formatInt(jobTotal)} jobs.`}
              </p>
            </div>
          </>
        )}
      </Card>

      {/* ---- intake: work that has arrived but not yet been processed ---- */}
      {workload.emails && (
        <Card className="p-5 animate-card-in" style={{ animationDelay: '160ms' }}>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Email intake</h2>
            <HelpTip>
              Documents arriving by monitored mailbox. Anything stuck pending is work that has not been
              processed yet. It has not consumed your allowance, and it will when it runs.
            </HelpTip>
            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800
                             text-gray-500 dark:text-gray-400 font-medium">
              {emailsArePeriodScoped ? `Last ${periodLabel}` : 'All time'}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4">
            {emailsArePeriodScoped
              ? 'Emails received in the selected period, by processing state.'
              : 'This source only supports 7- and 30-day windows, so longer periods show every email on record.'}
          </p>

          {(workload.emails.total || 0) === 0 ? (
            <EmptyState icon={Mail} title="No email intake in this window" />
          ) : (
            <>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                {formatInt(workload.emails.total)}
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-1.5">emails</span>
              </p>
              <div className="mt-4 space-y-2">
                {EMAIL_STATES.map(state => {
                  const count = workload.emails[state.key] || 0
                  const pct = workload.emails.total ? (count / workload.emails.total) * 100 : 0
                  return (
                    <div key={state.key} className="flex items-center gap-2">
                      <span className="text-[12px] text-gray-600 dark:text-gray-300 w-20">{state.label}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-[width] duration-700 ease-out ${state.bar}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[12px] font-semibold tabular-nums text-gray-900 dark:text-gray-100 w-10 text-right">
                        {formatInt(count)}
                      </span>
                    </div>
                  )
                })}
              </div>
              {workload.emails.failed > 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-3">
                  {formatInt(workload.emails.failed)} failed to ingest. Those documents never reached extraction.
                </p>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  )
}
